'use client'

import { useRouter } from "next/navigation";
import {
  useLocale,
  useLocalizedText,
} from "@/content/i18n/context";
import { getLocalizedPathname } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface UniversalPageHeroProps {
  readonly title: string;
  readonly breadcrumb?: string;
  readonly variant?: 'default' | 'gradient' | 'image';
  readonly backgroundImage?: string;
  readonly description?: string;
  readonly customContent?: React.ReactNode;
  readonly className?: string;
}

export default function UniversalPageHero({
  title,
  breadcrumb,
  variant = "default",
  backgroundImage,
  description,
  customContent,
  className = ""
}: UniversalPageHeroProps) {
  const router = useRouter();
  const locale = useLocale();
  const translatedTitle = useLocalizedText(title);
  const translatedBreadcrumb = breadcrumb ? useLocalizedText(breadcrumb) : "";
  const translatedDescription = description ? useLocalizedText(description) : "";
  const translatedHome = useLocalizedText("Home");

  const renderContent = () => (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
          {translatedTitle}
        </h1>
        {breadcrumb && (
          <div className="text-white/80 text-sm md:text-base mb-2">
            <span
              className="cursor-pointer hover:text-white"
              onClick={() => router.push(getLocalizedPathname("/", locale))}
            >
              {translatedHome}
            </span>
            {" / "}
            <span>{translatedBreadcrumb}</span>
          </div>
        )}
        {description && (
          <p className="mt-4 text-sm sm:text-base text-white/90 max-w-3xl mx-auto leading-relaxed px-2 sm:px-0">
            {translatedDescription}
          </p>
        )}
        {customContent}
      </div>
    </div>
  );

  if (variant === 'image' && backgroundImage) {
    return (
      <section className={cn("relative h-[450px] sm:h-[400px] md:h-[350px] lg:h-[300px] overflow-hidden", className)}>
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <img
          src={backgroundImage}
          alt={translatedTitle}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {renderContent()}
      </section>
    );
  }

  if (variant === 'gradient') {
    return (
      <section className={cn("relative h-[300px] overflow-hidden bg-primary", className)}>
        <div className="absolute inset-0 bg-gradient-to-r from-primary to-blue-700 opacity-90"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
        {renderContent()}
      </section>
    );
  }

  // Default variant - simple colored background
  return (
    <section className={cn("relative h-[200px] bg-primary", className)}>
      {renderContent()}
    </section>
  );
}
